import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async zoom(index, event) {
    // Retain the native index and event while a large gallery mounts its zoom UI.
    event.preventDefault();
    const zoomDialog = await this.#ensureZoomDialog();
    if (this.isConnected) zoomDialog?.open(index, event);
  }

  /** Keyboard-accessible control for the currently selected native slide. */
  zoomCurrent(event) {
    return this.zoom(this.slideshow?.current ?? 0, event);
  }

  /** Mount the current native zoom tree, including after section morphs. */
  async #ensureZoomDialog() {
    // Native variant morphs can retain this component while replacing its
    // children. The live DOM is authoritative; never cache a resolved dialog.
    let zoomDialog = this.querySelector(':scope > zoom-dialog');
    if (!zoomDialog) {
      const template = this.querySelector(':scope > template[data-tg-deferred-zoom]');
      if (!(template instanceof HTMLTemplateElement)) return undefined;
      const fragment = template.content.cloneNode(true);
      zoomDialog = fragment.querySelector('zoom-dialog');
      if (!zoomDialog) return undefined;
      // Mount synchronously before awaiting module readiness. A second click
      // sees this same tree, so it cannot create a second dialog.
      template.replaceWith(fragment);
    }

    await customElements.whenDefined('zoom-dialog');
    if (!this.isConnected || !zoomDialog.isConnected || zoomDialog.parentElement !== this) return undefined;
    this.updatedCallback();
    zoomDialog.updatedCallback();
    // addEventListener deduplicates the same callback on the same live target.
    zoomDialog.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal: this.#controller.signal,
    });
    return zoomDialog;
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
